import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

const VIDEO_SRC =
  "https://customer-assets.emergentagent.com/job_neon-narrative-hub/artifacts/120fu3c1_grok_video_2026-04-24-15-42-55.mp4";

const MeritPlatform = () => {
  const videoRef = useRef(null);
  const [textVisible, setTextVisible] = useState(false);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    let timer;
    const onPlaying = () => {
      timer = setTimeout(() => setTextVisible(true), 400);
    };
    v.addEventListener("playing", onPlaying, { once: true });
    const fallback = setTimeout(() => setTextVisible(true), 1200);
    const p = v.play?.();
    if (p && typeof p.catch === "function") {
      p.catch(() => setTextVisible(true));
    }
    return () => {
      v.removeEventListener("playing", onPlaying);
      clearTimeout(timer);
      clearTimeout(fallback);
    };
  }, []);

  const lineTransition = { duration: 1.1, ease: [0.22, 1, 0.36, 1] };

  return (
    <section
      id="merit"
      className="relative w-full overflow-hidden snap-section snap-reveal"
      data-testid="section-merit"
    >
      {/* Video */}
      <div className="relative w-full h-[70vh] min-h-[520px] md:h-[85vh] md:min-h-[640px]">
        <video
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-cover"
          src={VIDEO_SRC}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          data-testid="merit-video"
        />

        {/* Dark readability overlay */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(180deg, rgba(5,5,10,0.78) 0%, rgba(5,5,10,0.45) 20%, rgba(5,5,10,0.4) 60%, rgba(5,5,10,0.82) 100%)",
          }}
        />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at center, transparent 30%, rgba(5,5,10,0.55) 100%)",
          }}
        />

        {/* Top/bottom edge fades for smooth section transition */}
        <div
          className="absolute top-0 left-0 right-0 h-32 pointer-events-none"
          style={{ background: "linear-gradient(180deg, #05050A 0%, transparent 100%)" }}
        />
        <div
          className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none"
          style={{ background: "linear-gradient(0deg, #05050A 0%, transparent 100%)" }}
        />

        {/* Subtle scanlines */}
        <div
          className="absolute inset-0 pointer-events-none opacity-25"
          style={{
            backgroundImage:
              "repeating-linear-gradient(to bottom, rgba(255,255,255,0.04) 0px, rgba(255,255,255,0.04) 1px, transparent 1px, transparent 4px)",
            mixBlendMode: "overlay",
          }}
        />

        {/* Centered overlay copy */}
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={textVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={lineTransition}
            className="font-display font-black uppercase tracking-tight leading-[1] text-white"
            style={{
              fontSize: "clamp(1.75rem, 5vw, 4rem)",
              textShadow: "0 2px 20px rgba(0,0,0,0.7)",
            }}
            data-testid="merit-line-1"
          >
            A Platform for{" "}
            <span
              className="inline-block text-cyan-300 interactive-text interactive-text--yellow"
              style={{
                textShadow:
                  "0 0 22px rgba(0,240,255,0.65), 0 0 48px rgba(255,222,0,0.35)",
              }}
            >
              Merit
            </span>
            .
          </motion.div>

          <motion.div
            initial={{ opacity: 0, width: 0 }}
            animate={textVisible ? { opacity: 1, width: "120px" } : { opacity: 0, width: 0 }}
            transition={{ ...lineTransition, delay: 0.45 }}
            className="h-[1.5px] my-8 md:my-10"
            style={{
              background:
                "linear-gradient(90deg, transparent, #00F0FF 50%, transparent)",
              boxShadow: "0 0 10px rgba(0,240,255,0.55)",
            }}
          />

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={textVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 18 }}
            transition={{ ...lineTransition, delay: 0.65 }}
            className="font-display font-black uppercase tracking-tight leading-[1.02] text-white"
            style={{
              fontSize: "clamp(2rem, 7vw, 5.5rem)",
              textShadow: "0 2px 22px rgba(0,0,0,0.7)",
            }}
            data-testid="merit-line-2a"
          >
            Where{" "}
            <span
              className="inline-block text-fuchsia-300 interactive-text interactive-text--magenta"
              style={{
                textShadow:
                  "0 0 24px rgba(255,0,255,0.7), 0 0 54px rgba(0,240,255,0.35)",
              }}
            >
              Talent
            </span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={textVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 18 }}
            transition={{ ...lineTransition, delay: 0.95 }}
            className="font-display font-black uppercase tracking-tight leading-[1.02] text-white"
            style={{
              fontSize: "clamp(2rem, 7vw, 5.5rem)",
              textShadow: "0 2px 22px rgba(0,0,0,0.7)",
            }}
            data-testid="merit-line-2b"
          >
            Learns to Fly.
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default MeritPlatform;
